'use client';

import React, { FormEvent } from 'react';

type Props = {
  title?: string;
  description?: string;
  ctaLabel?: string;
  onSubmit?: (e: FormEvent<HTMLButtonElement>) => void;
  className?: string;
  children?: React.ReactNode;
};

export const NewsletterBanner: React.FC<Props> = ({
  title = 'Subscreva a nossa Newsletter',
  description = 'Esteja sempre a par das ações, iniciativas e histórias que promovem a inclusão e o desenvolvimento.',
  ctaLabel = 'SUBMETER',
  onSubmit,
  className = '',
  children,
}) => {
  const handleSubmit = (e: FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit(e);
    }
  };

  return (
    <section
      className={`relative w-full h-full py-8 lg:h-80 overflow-hidden ${className}`}
      style={{
        background: 'linear-gradient(33deg, #EA7F1F 0%, #F7941D 41%, #E63946 100%)',
      }}
    >
      {/* SVG Decorativos - Layer 1 */}
      <svg
        className="absolute left-[1089.19px] top-[90.73px] w-[500px] h-[122px] pointer-events-none"
        viewBox="0 0 500 122"
        fill="none"
      >
        <path d="M499.342 0H0V121.157H499.342V0Z" fill="#F15A29" fillOpacity="0.2" />
      </svg>

      {/* SVG Decorativos - Layer 2 (Diagonal esquerda) */}
      <svg
        className="absolute left-[912px] top-[-87.09px] w-[178px] h-[212px] pointer-events-none"
        viewBox="0 0 178 212"
        fill="none"
      >
        <path
          d="M177.189 211.895L0 34.0635V-87.0938L177.189 90.7382V211.895Z"
          fill="#EF4130"
          fillOpacity="0.2"
        />
      </svg>

      {/* SVG Decorativos - Layer 3 (Diagonal direita amarela) */}
      <svg
        className="absolute left-[1411.34px] top-[-87.09px] w-[178px] h-[212px] pointer-events-none"
        viewBox="0 0 178 212"
        fill="none"
      >
        <path
          d="M177.189 211.895L0 34.0635V-87.0938L177.189 90.7382V211.895Z"
          fill="#FFDE2F"
          fillOpacity="0.2"
        />
      </svg>

      {/* SVG Decorativos - Layer 4 (Horizontal superior) */}
      <svg
        className="absolute left-[912px] top-[-87.09px] w-[500px] h-[35px] pointer-events-none"
        viewBox="0 0 500 35"
        fill="none"
      >
        <path
          d="M499.342 -87.0938H0V34.0635H499.342V-87.0938Z"
          fill="#F7941D"
          fillOpacity="0.2"
        />
      </svg>

      {/* SVG Decorativos - Layer 5 (Diagonal meio esquerda) */}
      <svg
        className="absolute left-[912px] top-[73.15px] w-[178px] h-[271px] pointer-events-none"
        viewBox="0 0 178 271"
        fill="none"
      >
        <path
          d="M177.189 298.964L0 121.157V0L177.189 177.807V298.964Z"
          fill="#EF4130"
          fillOpacity="0.2"
        />
      </svg>

      {/* SVG Decorativos - Layer 6 (Horizontal meio) */}
      <svg
        className="absolute left-[912px] top-[73.15px] w-[500px] h-[122px] pointer-events-none"
        viewBox="0 0 500 122"
        fill="none"
      >
        <path d="M499.342 0H0V121.157H499.342V0Z" fill="#F7941D" fillOpacity="0.2" />
      </svg>

      {/* SVG Decorativos - Layer 7 (Horizontal inferior) */}
      <svg
        className="absolute left-[1089.19px] top-[250.96px] w-[500px] h-[94px] pointer-events-none"
        viewBox="0 0 500 94"
        fill="none"
      >
        <path d="M499.342 0H0V121.157H499.342V0Z" fill="#F15A29" fillOpacity="0.2" />
      </svg>

      {/* SVG Decorativos - Layer 8 (Diagonal meio direita amarela) */}
      <svg
        className="absolute left-[1411.34px] top-[73.15px] w-[178px] h-[271px] pointer-events-none"
        viewBox="0 0 178 271"
        fill="none"
      >
        <path
          d="M177.189 298.964L0 121.157V0L177.189 177.807V298.964Z"
          fill="#FFDE2F"
          fillOpacity="0.2"
        />
      </svg>

      {/* SVG Decorativos - Layer 9 (Chevron/Onda) */}
      <svg
        className="absolute left-[987.93px] top-[317.39px] w-[102px] h-[27px] pointer-events-none"
        viewBox="0 0 102 27"
        fill="none"
      >
        <path
          d="M97.3186 96.3121C87.0081 82.0187 70.0882 64.1083 49.7902 44.1524C32.841 27.5142 16.2736 13.0212 2.43811 2.44462L0 0V152.064C14.3055 162.79 31.8129 177.932 49.7609 195.593C71.7038 217.17 89.4756 237.675 99.4924 252.267L101.255 254.039V101.625L97.3186 96.3121Z"
          fill="#EF4130"
          fillOpacity="0.2"
        />
      </svg>

      {/* Conteúdo Principal - Posicionado com z-index */}
      <div className="relative z-10 h-full flex items-center px-8 md:px-12 container mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
          {/* Esquerda: Título e Botão */}
          <div className="space-y-6 flex flex-col justify-center">
            <h2
              className="text-5xl md:text-5xl font-medium leading-tight text-white"
              style={{ fontFamily: "'Barlow Semi Condensed', sans-serif" }}
              role="heading"
              aria-level={2}
            >
              {title}
            </h2>

            <button
              onClick={handleSubmit}
              className="cursor-pointer px-7 py-1.5 border-2 border-white text-white hover:bg-white hover:text-orange-600 font-medium text-base rounded transition-all duration-300 ease-in-out shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 uppercase tracking-wider w-fit"
              style={{ fontFamily: "'Barlow Semi Condensed', sans-serif" }}
              type="button"
              aria-label={ctaLabel}
            >
              {ctaLabel}
            </button>

            {children && (
              <div className="pt-2">
                {children}
              </div>
            )}
          </div>

          {/* Direita: Descrição */}
          <div className="flex items-center justify-start">
            <div
              className="text-2xl text-white leading-relaxed max-w-md text-left"
              style={{
                fontFamily: "'Nunito Sans', sans-serif",
                fontWeight: 300,
                lineHeight: '2rem',
              }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterBanner;